/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Stage, Layer as KonvaLayer, Image as KonvaImage, Line, Rect as KonvaRect, Group as KonvaGroup, Transformer } from 'react-konva';
import { getStroke } from 'perfect-freehand';
import {
  Loader2, Save, X, Brush, Eraser, Trash2, Eye, EyeOff,
  Lock, Unlock, Undo2, Redo2,
  Image as ImageIcon, Library as LibraryIcon, Sparkles, Folder, ChevronRight,
  ArrowDown, Crop, RotateCcw, FlipHorizontal, FlipVertical, Scaling, RectangleHorizontal, GripVertical,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  saveShotScene, uploadSceneAsset, listAllLibraryItems, listFolders, generateFromSceneImage,
  importLibraryItems, moveLibraryItem,
} from '../lib/api';

// Blanking (letterbox / pillarbox) ratios — same as DaVinci Resolve output blanking.
const BLANKING_RATIOS = [
  { value: '1.33', label: '1.33  (4:3)' },
  { value: '1.66', label: '1.66' },
  { value: '1.77', label: '1.77  (16:9)' },
  { value: '1.85', label: '1.85  (Flat)' },
  { value: '2.0',  label: '2.0' },
  { value: '2.35', label: '2.35  (Scope)' },
  { value: '2.39', label: '2.39  (Scope)' },
  { value: '2.40', label: '2.40  (Scope)' },
];

// Compute black-bar geometry for a blanking ratio inside a canvas of size (cw, ch).
// Returns null (no bars needed), letterbox bars (top/bottom), or pillarbox bars (left/right).
function blankingBars(ratio, cw, ch) {
  const r = parseFloat(ratio);
  if (!r || r <= 0) return null;
  const canvasRatio = cw / ch;
  if (Math.abs(r - canvasRatio) < 0.01) return null; // essentially same ratio
  if (r > canvasRatio) {
    // Letterbox — horizontal bars top & bottom
    const barH = Math.max(0, (ch - cw / r) / 2);
    return barH > 0 ? { type: 'letter', barH } : null;
  }
  // Pillarbox — vertical bars left & right
  const barW = Math.max(0, (cw - ch * r) / 2);
  return barW > 0 ? { type: 'pillar', barW } : null;
}

// Default scene size (16:9). The component overrides with format-aware dims.
const SCENE_W = 1920;
const SCENE_H = 1080;
const SCENE_VERSION = 1;

// Derive scene dimensions from a format string ('16:9' or '9:16').
const sceneDims = (fmt) => fmt === '9:16'
  ? { w: 1080, h: 1920 }
  : { w: 1920, h: 1080 };

const HISTORY_LIMIT = 50;

// Fixed grayscale palette — keeps boards tonally consistent and avoids the
// noise of a full color picker. Order matches usual storyboard ink choices
// from solid black (line work) to whites (highlights).
const GRAYSCALE_PALETTE = [
  '#000000', '#333333', '#555555', '#777777',
  '#999999', '#bbbbbb', '#dddddd', '#ffffff',
];

const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `id_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

const IDENTITY_TRANSFORM = { x: SCENE_W / 2, y: SCENE_H / 2, rotation: 0, scaleX: 1, scaleY: 1 };
const getTransform = (layer) => layer.transform || IDENTITY_TRANSFORM;

const makeDrawingLayer = (name = 'Calque 1') => ({
  id: uid(),
  name,
  type: 'drawing',
  visible: true,
  locked: false,
  // Drawing layers sit in a Konva.Group anchored at the scene centre so the
  // user can later move / scale / rotate the whole stack at once. Strokes
  // are stored in the group's local space.
  transform: { ...IDENTITY_TRANSFORM },
  data: { strokes: [], cropRect: null },
});

// Image layer: scene-asset URL plus its intrinsic pixel size (used to render
// at 1:1 by default). Transform is applied via Konva (x/y are the centre of
// the image since we set offset to half-size).
const makeImageLayer = ({ name, url, width, height, x, y }) => ({
  id: uid(),
  name: name || 'Image',
  type: 'image',
  visible: true,
  locked: false,
  // eraseStrokes — same format as drawing-layer strokes, rendered with
  //   destination-out inside the KonvaGroup so they make the image transparent.
  // cropRect — {x, y, width, height} in image-local pixel coords (0..w, 0..h);
  //   applied as clipFunc so area outside is transparent.
  data: { url, width, height, eraseStrokes: [], cropRect: null },
  transform: { x, y, rotation: 0, scaleX: 1, scaleY: 1 },
});

// Module-level cache of HTMLImageElement keyed by URL, plus a per-URL set of
// callbacks waiting for the load to finish. Konva.Image needs an actual <img>
// so callers share the same cached element. HTTP / relative URLs go through
// fetch + FileReader so the eventual <img> holds a data: URL — sidesteps the
// canvas tainting that would otherwise break stage.toBlob at save time.
//
// Important: a callback only fires when the image *transitions* to loaded.
// If the cached image is already complete on a subsequent loadImage call,
// no callback fires — otherwise a setState in the listener would loop the
// renderer indefinitely (render → loadImage → onLoad → setState → render…).
const imageCache = new Map();
const imageListeners = new Map();

function subscribeImage(url, onLoad) {
  if (!onLoad) return;
  let set = imageListeners.get(url);
  if (!set) { set = new Set(); imageListeners.set(url, set); }
  set.add(onLoad);
}

function fireImageListeners(url) {
  const set = imageListeners.get(url);
  if (!set) return;
  imageListeners.delete(url);
  for (const fn of set) {
    try { fn(); } catch { /* swallow listener exceptions */ }
  }
}

function loadImage(url, onLoad) {
  if (!url) return null;
  // Convert absolute Railway URLs to relative paths so the fetch goes through
  // the Vercel proxy (same-origin) — avoids CORS entirely.
  // Uses hostname detection, not env var (env var may be absent in Vercel build).
  if (typeof url === 'string' && url.startsWith('http')) {
    try {
      const _u = new URL(url);
      if (_u.hostname.endsWith('.railway.app')) {
        url = _u.pathname + _u.search;
      }
    } catch { /* keep url as-is */ }
  }
  const cached = imageCache.get(url);
  if (cached) {
    if (!(cached.complete && cached.naturalWidth > 0)) {
      subscribeImage(url, onLoad);
    }
    return cached;
  }
  const img = new window.Image();
  imageCache.set(url, img);
  subscribeImage(url, onLoad);
  if (typeof url === 'string' && url.startsWith('data:')) {
    img.onload = () => fireImageListeners(url);
    img.src = url;
    return img;
  }
  // fetch → data URL: avoids canvas tainting (stage.toBlob works for saves/exports).
  // URLs normalised to relative paths above → same-origin fetch via Vercel proxy,
  // no CORS headers needed. Absolute URLs use Railway CORS (app.kinote.io allowed).
  fetch(url, { credentials: 'include' })
    .then((res) => res.blob())
    .then((blob) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    }))
    .then((dataUrl) => {
      img.onload = () => fireImageListeners(url);
      img.src = dataUrl;
    })
    .catch(() => fireImageListeners(url));
  return img;
}

// Render a single freehand stroke as a filled Konva.Line. The eraser variant
// uses destination-out so it cuts only through strokes inside the same
// Konva.Layer (one per editor layer, for clean per-layer erasing).
const StrokeShape = memo(function StrokeShape({ stroke }) {
  const points = useMemo(() => {
    const outline = getStroke(stroke.points, {
      size: stroke.size,
      thinning: 0.5,
      smoothing: 0.5,
      streamline: 0.5,
      simulatePressure: true,
    });
    const flat = [];
    for (const [x, y] of outline) { flat.push(x, y); }
    return flat;
  }, [stroke.points, stroke.size]);
  return (
    <Line
      points={points}
      closed
      fill={stroke.color}
      listening={false}
      perfectDrawEnabled={false}
      globalCompositeOperation={stroke.mode === 'erase' ? 'destination-out' : 'source-over'}
    />
  );
});

function LayerRow({ layer, isActive, onSelect, onToggleVisible, onToggleLock, onRename, onDelete, onDragStart, onDragOver, onDrop, onDragEnd, isDragTarget }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(layer.name);
  useEffect(() => { setDraft(layer.name); }, [layer.name]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== layer.name) onRename(trimmed);
    else setDraft(layer.name);
  };

  return (
    <div
      draggable
      onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData('application/x-kinote-layer', layer.id); e.dataTransfer.effectAllowed = 'move'; onDragStart?.(); }}
      onDragOver={(e) => { if (e.dataTransfer.types.includes('application/x-kinote-layer')) { e.preventDefault(); e.stopPropagation(); onDragOver?.(); } }}
      onDrop={(e) => { if (e.dataTransfer.types.includes('application/x-kinote-layer')) { e.preventDefault(); e.stopPropagation(); onDrop?.(); } }}
      onDragEnd={() => onDragEnd?.()}
      className={`group flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer text-xs transition-all ${isDragTarget ? 'border-t-2 border-primary' : ''} ${isActive ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-secondary/40'}`}
      onClick={() => onSelect(layer.id)}
      data-testid={`layer-row-${layer.id}`}
    >
      <GripVertical className="w-3 h-3 text-muted-foreground/30 shrink-0 cursor-grab" />
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground"
        onClick={(e) => { e.stopPropagation(); onToggleVisible(); }}
        title={layer.visible ? 'Masquer' : 'Afficher'}
      >
        {layer.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      </button>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground"
        onClick={(e) => { e.stopPropagation(); onToggleLock(); }}
        title={layer.locked ? 'Déverrouiller' : 'Verrouiller'}
      >
        {layer.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
      </button>
      {editing ? (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setDraft(layer.name); setEditing(false); }
          }}
          onClick={(e) => e.stopPropagation()}
          className="h-6 text-xs px-1.5"
        />
      ) : (
        <span
          className="flex-1 truncate"
          onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
          title="Double-cliquez pour renommer"
        >
          {layer.name}
        </span>
      )}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          className="p-0.5 text-muted-foreground hover:text-destructive"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Supprimer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function ShotEditorModal({ open, onOpenChange, projectId, shotId, shotData, onSaved, format = '16:9', blanking = null, onBlankingChange }) {
  // Dynamic scene dimensions based on project format.
  const { w: sceneW, h: sceneH } = sceneDims(format);
  // Local overrides of the module-level constants so all existing code in
  // the component body uses the format-aware values without further changes.
  // eslint-disable-next-line no-unused-vars
  const identityTransform = { x: sceneW / 2, y: sceneH / 2, rotation: 0, scaleX: 1, scaleY: 1 };
  // Shadow the module-level getTransform with a format-aware version.
  const getTransform = (layer) => layer.transform || identityTransform; // eslint-disable-line no-shadow
  // Format-aware drawing layer factory.
  const newDrawingLayer = (name = 'Calque 1') => ({
    id: uid(), name, type: 'drawing', visible: true, locked: false,
    transform: { ...identityTransform },
    data: { strokes: [] },
  });

  // Scene state — single source of truth for layers + background.
  const [layers, setLayers] = useState([]);
  const [activeLayerId, setActiveLayerId] = useState(null);

  // Tool + brush state. Storyboard convention: only grayscale ink. We surface
  // a fixed palette (no full color picker) so the boards stay tonally
  // consistent across plans. The 'select' tool mutes the brush so the user
  // can grab and reshape an imported image instead of drawing over it.
  const [tool, setTool] = useState('brush'); // 'brush' | 'eraser' | 'transform' | 'crop'
  const [brushSize, setBrushSize] = useState(20);
  const [brushColor, setBrushColor] = useState('#000000');

  // Undo/redo stacks of *layers* snapshots. Background isn't tracked since
  // it's locked in V1 — only the user-editable layers go through history.
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);

  const [saving, setSaving] = useState(false);
  const [dragLayerId, setDragLayerId] = useState(null);
  const [dragOverLayerId, setDragOverLayerId] = useState(null);
  // Layers panel can collapse to give the canvas the full width when the user
  // doesn't need to manage layers — important on smaller monitors where 288px
  // of side panel kept the 16:9 stage from spreading.

  // In-progress stroke kept in a ref to avoid re-rendering the whole tree on
  // every pointermove; a tick counter triggers the re-render at a controlled
  // rate (raf-throttled below).
  const drawingRef = useRef(false);
  const currentStrokeRef = useRef(null);
  const rafPendingRef = useRef(false);
  const [tick, setTick] = useState(0);

  // Crop-selection state (scene coordinates, updated during mouse drag)
  const [cropStart, setCropStart] = useState(null);
  const [cropCurrent, setCropCurrent] = useState(null);

  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const [stageDims, setStageDims] = useState({ w: 0, h: 0 });
  const stageScale = stageDims.w / sceneW || 1;

  // Refs to the actual Konva nodes for image layers, so the Transformer can
  // attach to whichever layer is currently selected.
  const shapeRefs = useRef({});
  const transformerRef = useRef(null);

  // Hidden file input for the "Importer une image" toolbar button.
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // AI generation from current canvas composition.
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  // "Capturer" button state — capture current canvas → save to library.
  const [capturing, setCapturing] = useState(false);

  // Library panel: all items + folders loaded once on open, navigated client-side.
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryFolders, setLibraryFolders] = useState([]);
  const [expandedFolderIds, setExpandedFolderIds] = useState(new Set());
  const [layerDropActive, setLayerDropActive] = useState(false);
  // Drop highlight in the editor library panel: folder id, or 'root' for the
  // header (move to root). Used to give visual feedback when dragging an item
  // between folders.
  const [libraryDropTargetId, setLibraryDropTargetId] = useState(null);
  // Per-user hide-list for admin-public folders, persisted in localStorage.
  // Hiding a public folder doesn't remove it — it just greys it out so the
  // user can de-emphasize templates they don't use day-to-day.
  const HIDDEN_PUBLIC_LS_KEY = 'kinote.editor.hiddenPublicFolders';
  const [hiddenPublicFolderIds, setHiddenPublicFolderIds] = useState(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_PUBLIC_LS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch { return new Set(); }
  });
  const togglePublicFolderHidden = useCallback((id) => {
    setHiddenPublicFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(HIDDEN_PUBLIC_LS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const activeLayer = useMemo(
    () => layers.find((l) => l.id === activeLayerId) || null,
    [layers, activeLayerId],
  );

  // Tool-specific cursors built as inline SVG data URLs. The radius tracks
  // brushSize × stageScale so the cursor visually matches what will be drawn
  // (clamped — most browsers reject cursors above ~32px).
  const brushCursor = useMemo(() => {
    const r = Math.max(2, Math.min((brushSize * stageScale) / 2, 14));
    const size = Math.ceil(r * 2 + 2);
    const c = size / 2;
    const fill = (brushColor || '#000000').replace('#', '%23');
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'><circle cx='${c}' cy='${c}' r='${r}' fill='${fill}'/></svg>`;
    return `url("data:image/svg+xml,${svg}") ${c} ${c}, crosshair`;
  }, [brushSize, brushColor, stageScale]);

  const eraserCursor = useMemo(() => {
    const r = Math.max(3, Math.min((brushSize * stageScale) / 2, 14));
    const size = Math.ceil(r * 2 + 2);
    const c = size / 2;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'><circle cx='${c}' cy='${c}' r='${r}' fill='white' stroke='black' stroke-width='1'/></svg>`;
    return `url("data:image/svg+xml,${svg}") ${c} ${c}, crosshair`;
  }, [brushSize, stageScale]);

  // ---- Load library items + folders on open ----
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.all([
      listAllLibraryItems().catch(() => []),
      listFolders().catch(() => []),
    ]).then(([items, folders]) => {
      if (cancelled) return;
      setLibraryItems(Array.isArray(items) ? items : []);
      setLibraryFolders(Array.isArray(folders) ? folders : []);
      setExpandedFolderIds(new Set());
    });
    return () => { cancelled = true; };
  }, [open]);

  // ---- Drag & drop a library item into the layer stack ----
  // Two MIMEs share the same drag: a "copy" payload used by the canvas /
  // layer-list drops (creates a new image layer), and a "move" payload used
  // by folder drop targets inside the library panel (relocates the item).
  // The browser exposes both — each drop handler reads only what it cares
  // about, so the same drag works for either intent.
  const DND_MIME = 'application/x-kinote-library';
  const DND_MIME_MOVE = 'application/x-kinote-library-move';

  const onLibraryDragStart = (item) => (e) => {
    e.dataTransfer.setData(DND_MIME, JSON.stringify({
      canonical_url: item.canonical_url,
      name: item.name,
    }));
    e.dataTransfer.setData(DND_MIME_MOVE, JSON.stringify({
      id: item.id,
      kind: item.kind,
      from_folder_id: item.folder_id || null,
    }));
    e.dataTransfer.effectAllowed = 'copyMove';
  };

  // Folder / root drop targets in the library panel. The handlers only react
  // to the "move" MIME so a drag from outside (no payload) is ignored, and
  // dropping back onto the same folder is a no-op.
  const onLibraryFolderDragOver = (folderId) => (e) => {
    const types = Array.from(e.dataTransfer.types || []);
    if (!types.includes(DND_MIME_MOVE)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    const key = folderId === null ? 'root' : folderId;
    if (libraryDropTargetId !== key) setLibraryDropTargetId(key);
  };
  const onLibraryFolderDragLeave = (folderId) => (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    const key = folderId === null ? 'root' : folderId;
    if (libraryDropTargetId === key) setLibraryDropTargetId(null);
  };
  const onLibraryFolderDrop = (folderId) => async (e) => {
    const types = Array.from(e.dataTransfer.types || []);
    if (!types.includes(DND_MIME_MOVE)) return;
    e.preventDefault();
    e.stopPropagation();
    setLibraryDropTargetId(null);
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData(DND_MIME_MOVE)); } catch { return; }
    if (!payload?.id || !payload?.kind) return;
    const target = folderId || null;
    if ((payload.from_folder_id || null) === target) return;
    try {
      await moveLibraryItem(payload.kind, payload.id, target);
      setLibraryItems((prev) => prev.map((it) => (
        it.id === payload.id ? { ...it, folder_id: target } : it
      )));
      toast.success('Déplacé');
    } catch {
      toast.error('Échec du déplacement');
    }
  };

  const onLayerListDragOver = (e) => {
    if (Array.from(e.dataTransfer.types || []).includes(DND_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (!layerDropActive) setLayerDropActive(true);
    }
  };

  const onLayerListDragLeave = () => {
    if (layerDropActive) setLayerDropActive(false);
  };

  // Shared logic: create an image layer from a serialized library payload.
  // Used by both the layer-list drop and the canvas drop.
  const addLibraryItemFromPayload = (raw) => {
    if (!raw) return;
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    if (!payload?.canonical_url) return;
    let url = payload.canonical_url;
    if (typeof url === 'string' && url.startsWith('http')) {
      try {
        const _u = new URL(url);
        if (_u.hostname.endsWith('.railway.app')) url = _u.pathname + _u.search;
      } catch { /* keep url as-is */ }
    }
    const placeholder = makeImageLayer({
      name: payload.name || 'Image',
      url,
      width: sceneW,
      height: sceneH,
      x: sceneW / 2,
      y: sceneH / 2,
    });
    // Probe natural size and adjust the transform so the imported asset
    // lands at ~80% of the smaller canvas dimension.
    loadImage(url, () => {
      const img = imageCache.get(url);
      if (!img || !img.naturalWidth) return;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const ratio = Math.min(sceneW / w, sceneH / h) * 0.8;
      setLayers((prev) => prev.map((l) => (
        l.id === placeholder.id
          ? {
              ...l,
              data: { ...l.data, width: w, height: h },
              transform: { ...l.transform, scaleX: ratio, scaleY: ratio },
            }
          : l
      )));
    });
    pushHistory([...layers, placeholder]);
    setActiveLayerId(placeholder.id);
    setTool('transform');
  };

  const onLayerListDrop = (e) => {
    e.preventDefault();
    setLayerDropActive(false);
    addLibraryItemFromPayload(e.dataTransfer.getData(DND_MIME));
  };

  // Canvas accepts the same library DnD payload. Kept stateless on purpose:
  // any visual cue here would re-render the container that ResizeObserver is
  // watching, and we already had a hard freeze the last time we did that.
  // The native dropEffect = 'copy' cursor is enough feedback.
  const onCanvasDragOver = (e) => {
    if (Array.from(e.dataTransfer.types || []).includes(DND_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const onCanvasDrop = (e) => {
    if (!Array.from(e.dataTransfer.types || []).includes(DND_MIME)) return;
    e.preventDefault();
    addLibraryItemFromPayload(e.dataTransfer.getData(DND_MIME));
  };

  // ---- Init from shotData on open ----
  useEffect(() => {
    if (!open || !shotData) return;
    const sc = shotData.scene;
    if (sc && Array.isArray(sc.layers)) {
      // Migration: legacy scenes carrying a separate background_url get it
      // promoted to a real image layer at the bottom of the stack so the
      // user can manipulate it like any other image.
      let initial = sc.layers;
      if (sc.background_url && !initial.some((l) => l.type === 'image' && l.data?.url === sc.background_url)) {
        initial = [
          makeImageLayer({
            name: 'Image',
            url: sc.background_url,
            width: sceneW,
            height: sceneH,
            x: sceneW / 2,
            y: sceneH / 2,
          }),
          ...initial,
        ];
      }
      setLayers(initial);
      const firstUnlocked = initial.find((l) => !l.locked);
      setActiveLayerId(firstUnlocked?.id ?? initial[0]?.id ?? null);
    } else {
      // No scene yet: bootstrap with the thumbnail as a regular image layer
      // (so the user can hide / delete / move / scale it like any other),
      // plus an empty drawing layer ready for input.
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const drawing = newDrawingLayer('Calque 1');
      if (shotData.frame_path) {
        const url = apiBase + shotData.frame_path;
        const image = makeImageLayer({
          name: 'Image',
          url,
          width: sceneW,
          height: sceneH,
          x: sceneW / 2,
          y: sceneH / 2,
        });
        // Probe the natural size so the layer renders at the proper aspect
        // ratio. The listener fires once when the image actually arrives.
        loadImage(url, () => {
          const img = imageCache.get(url);
          if (!img || !img.naturalWidth) return;
          const w = img.naturalWidth;
          const h = img.naturalHeight;
          const ratio = Math.min(sceneW / w, sceneH / h);
          setLayers((prev) => prev.map((l) => (
            l.id === image.id
              ? {
                  ...l,
                  data: { ...l.data, width: w, height: h },
                  transform: { ...l.transform, scaleX: ratio, scaleY: ratio },
                }
              : l
          )));
        });
        setLayers([image, drawing]);
        setActiveLayerId(drawing.id);
      } else {
        setLayers([drawing]);
        setActiveLayerId(drawing.id);
      }
    }
    setPast([]);
    setFuture([]);
    setTool('brush');
  }, [open, shotData]);

  // ---- Fit stage to container while keeping a strict 16:9 envelope ----
  // We compute the largest 16:9 box that fits in the container so the canvas
  // visually matches the storyboard thumbnail aspect, and the Stage occupies
  // exactly that box (no half-baked centring with a wider/taller frame).
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const PADDING = 8;
    const update = () => {
      const el = containerRef.current;
      if (!el) return;
      const aw = Math.max(0, el.clientWidth - PADDING * 2);
      const ah = Math.max(0, el.clientHeight - PADDING * 2);
      if (aw <= 0 || ah <= 0) return;
      const ratio = sceneW / sceneH;
      let w = aw;
      let h = w / ratio;
      if (h > ah) { h = ah; w = h * ratio; }
      setStageDims({ w: Math.floor(w), h: Math.floor(h) });
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    schedule();
    const ro = new ResizeObserver(schedule);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [open]);

  // ---- Force-redraw all Konva layers after the stage first appears ----
  // Covers images already in the module-level imageCache: they are returned
  // immediately by loadImage (no onLoad callback, no setTick), so Konva
  // never gets an explicit draw signal. Use synchronous draw() via
  // getChildren() which is more reliable than find()+batchDraw() (async).
  useEffect(() => {
    if (!open || stageDims.w === 0) return;
    const id = requestAnimationFrame(() => {
      if (!stageRef.current) return;
      stageRef.current.getChildren().forEach((l) => l.draw?.());
    });
    return () => cancelAnimationFrame(id);
  }, [open, stageDims.w]);

  // ---- Layer mutations: every change snapshots `layers` into `past` first ----
  const pushHistory = useCallback((nextLayers) => {
    setPast((prev) => {
      const trimmed = prev.length >= HISTORY_LIMIT ? prev.slice(prev.length - HISTORY_LIMIT + 1) : prev;
      return [...trimmed, layers];
    });
    setFuture([]);
    setLayers(nextLayers);
  }, [layers]);

  const undo = useCallback(() => {
    setPast((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setFuture((f) => [layers, ...f]);
      setLayers(last);
      return prev.slice(0, -1);
    });
  }, [layers]);

  const redo = useCallback(() => {
    setFuture((prev) => {
      if (prev.length === 0) return prev;
      const next = prev[0];
      setPast((p) => [...p, layers]);
      setLayers(next);
      return prev.slice(1);
    });
  }, [layers]);

  const addLayer = () => {
    const next = newDrawingLayer(`Calque ${layers.length + 1}`);
    pushHistory([...layers, next]);
    setActiveLayerId(next.id);
  };

  const deleteLayer = (id) => {
    const next = layers.filter((l) => l.id !== id);
    pushHistory(next);
    if (activeLayerId === id) {
      setActiveLayerId(next[next.length - 1]?.id ?? null);
    }
  };

  const renameLayer = (id, name) => {
    pushHistory(layers.map((l) => (l.id === id ? { ...l, name } : l)));
  };

  const toggleVisible = (id) => {
    pushHistory(layers.map((l) => (l.id === id ? { ...l, visible: !l.visible } : l)));
  };

  const toggleLock = (id) => {
    pushHistory(layers.map((l) => (l.id === id ? { ...l, locked: !l.locked } : l)));
  };

  // Move layer up means *higher in the visual stack* — last in the array.
  const moveLayer = (id, delta) => {
    const idx = layers.findIndex((l) => l.id === id);
    if (idx < 0) return;
    const target = idx + delta;
    if (target < 0 || target >= layers.length) return;
    const copy = [...layers];
    const [moved] = copy.splice(idx, 1);
    copy.splice(target, 0, moved);
    pushHistory(copy);
  };

  // ---- Flip / mirror --------------------------------------------------------
  const flipLayer = (axis) => {
    if (!activeLayerId) return;
    pushHistory(layers.map((l) => {
      if (l.id !== activeLayerId) return l;
      const t = getTransform(l);
      return axis === 'h'
        ? { ...l, transform: { ...t, scaleX: -(t.scaleX ?? 1) } }
        : { ...l, transform: { ...t, scaleY: -(t.scaleY ?? 1) } };
    }));
  };

  // ---- Drawing handlers ----
  const scheduleRender = () => {
    if (rafPendingRef.current) return;
    rafPendingRef.current = true;
    requestAnimationFrame(() => {
      rafPendingRef.current = false;
      setTick((t) => t + 1);
    });
  };

  // Pointer position expressed in the local space of the active drawing
  // layer's Group, so a stroke added to a moved/rotated/scaled layer lands
  // visually under the cursor. Falls back to plain scene coords when the
  // layer hasn't mounted yet (group ref still null).
  const localPointer = (stage, layerId) => {
    const p = stage.getPointerPosition();
    if (!p) return null;
    const group = layerId && shapeRefs.current[layerId];
    if (group && typeof group.getAbsoluteTransform === 'function') {
      const t = group.getAbsoluteTransform().copy().invert();
      const lp = t.point(p);
      return [lp.x, lp.y, 0.5];
    }
    return [p.x / stageScale, p.y / stageScale, 0.5];
  };

  const onStageMouseDown = (e) => {
    if (tool === 'transform') return;

    // ── Crop tool ────────────────────────────────────────────────────────────
    if (tool === 'crop') {
      if (!activeLayer) return;
      const p = stageRef.current?.getPointerPosition();
      if (!p) return;
      const s = { x: p.x / stageScale, y: p.y / stageScale };
      setCropStart(s);
      setCropCurrent(s);
      return;
    }

    if (!activeLayer || activeLayer.locked || !activeLayer.visible) {
      toast.error(activeLayer?.locked ? 'Calque verrouillé.' : 'Sélectionnez un calque visible.');
      return;
    }

    // Brush only on drawing layers; eraser also works on image layers.
    if (tool === 'brush' && activeLayer.type !== 'drawing') {
      toast.error('Le pinceau ne fonctionne que sur un calque de dessin.');
      return;
    }
    if (tool === 'eraser' && activeLayer.type !== 'drawing' && activeLayer.type !== 'image') {
      toast.error('Sélectionnez un calque dessin ou image.');
      return;
    }

    // Don't start a stroke when the user actually grabbed a draggable node.
    if (e.target && e.target !== e.target.getStage()) return;
    const pt = localPointer(e.target.getStage(), activeLayerId);
    if (!pt) return;
    drawingRef.current = true;
    currentStrokeRef.current = {
      id: uid(),
      points: [pt],
      size: brushSize,
      color: brushColor,
      mode: tool === 'eraser' ? 'erase' : 'draw',
    };
    scheduleRender();
  };

  const onStageMouseMove = (e) => {
    // Crop selection update
    if (tool === 'crop' && cropStart) {
      const p = stageRef.current?.getPointerPosition();
      if (p) setCropCurrent({ x: p.x / stageScale, y: p.y / stageScale });
      return;
    }
    if (!drawingRef.current || !currentStrokeRef.current) return;
    const pt = localPointer(e.target.getStage(), activeLayerId);
    if (!pt) return;
    currentStrokeRef.current.points.push(pt);
    scheduleRender();
  };

  const onStageMouseUp = () => {
    // ── Finalise crop selection ────────────────────────────────────────────
    if (tool === 'crop' && cropStart && cropCurrent) {
      if (activeLayer) {
        const t = activeLayer.transform || identityTransform;
        const w = activeLayer.type === 'image' ? (activeLayer.data?.width || sceneW) : sceneW;
        const h = activeLayer.type === 'image' ? (activeLayer.data?.height || sceneH) : sceneH;
        const rad = -((t.rotation || 0) * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const toLocal = (sc) => {
          const dx = sc.x - t.x;
          const dy = sc.y - t.y;
          return {
            x: (dx * cos - dy * sin) / (t.scaleX || 1) + w / 2,
            y: (dx * sin + dy * cos) / (t.scaleY || 1) + h / 2,
          };
        };
        const localA = toLocal(cropStart);
        const localB = toLocal(cropCurrent);
        const rx = Math.max(0, Math.min(localA.x, localB.x));
        const ry = Math.max(0, Math.min(localA.y, localB.y));
        const rw = Math.min(w - rx, Math.abs(localB.x - localA.x));
        const rh = Math.min(h - ry, Math.abs(localB.y - localA.y));
        if (rw > 2 && rh > 2) {
          pushHistory(layers.map((l) =>
            l.id === activeLayerId
              ? { ...l, data: { ...l.data, cropRect: { x: rx, y: ry, width: rw, height: rh } } }
              : l
          ));
        }
      }
      setCropStart(null);
      setCropCurrent(null);
      setTool('transform');
      return;
    }

    if (!drawingRef.current) return;
    drawingRef.current = false;
    const stroke = currentStrokeRef.current;
    currentStrokeRef.current = null;
    if (!stroke || stroke.points.length === 0 || !activeLayerId) {
      scheduleRender();
      return;
    }

    if (activeLayer?.type === 'image' && stroke.mode === 'erase') {
      // Erase stroke on image layer → stored in data.eraseStrokes
      pushHistory(layers.map((l) => (
        l.id === activeLayerId
          ? { ...l, data: { ...l.data, eraseStrokes: [...(l.data?.eraseStrokes || []), stroke] } }
          : l
      )));
    } else {
      // Drawing layer stroke → data.strokes
      pushHistory(layers.map((l) => (
        l.id === activeLayerId
          ? { ...l, data: { ...l.data, strokes: [...(l.data?.strokes || []), stroke] } }
          : l
      )));
    }
  };

  // ---- Image layer transforms (drag, scale, rotate) ----
  const updateLayerTransform = useCallback((id, patch) => {
    pushHistory(layers.map((l) => (
      l.id === id
        ? { ...l, transform: { ...(l.transform || {}), ...patch } }
        : l
    )));
  }, [layers, pushHistory]);

  // Sync the Konva.Transformer with whichever layer is currently active.
  // Both image and drawing layers get transform handles in select mode;
  // locked / hidden layers don't.
  useEffect(() => {
    const tr = transformerRef.current;
    if (!tr) return;
    const target = layers.find((l) => l.id === activeLayerId);
    const eligible = target && (target.type === 'image' || target.type === 'drawing')
      && !target.locked && target.visible && (tool === 'transform');
    const node = eligible ? shapeRefs.current[target.id] : null;
    tr.nodes(node ? [node] : []);
    tr.getLayer()?.batchDraw();
  }, [activeLayerId, layers, tool]);

  // ---- Image import ----
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    if (!projectId || !shotId) {
      toast.error("Enregistrez d'abord le plan avant d'importer une image.");
      return;
    }
    setImporting(true);
    const tid = toast.loading('Import en cours…');
    try {
      const { url, width, height } = await uploadSceneAsset(projectId, shotId, file);
      // Default placement: fit inside the scene at 80% of the smaller axis,
      // centred. Anchor x/y at the centre because we render with offset = w/2.
      const targetLong = Math.min(sceneW, sceneH) * 0.8;
      const ratio = (width && height) ? (targetLong / Math.max(width, height)) : 1;
      const placedW = (width || sceneW) * ratio;
      const placedH = (height || sceneH) * ratio;
      const layer = makeImageLayer({
        name: `Image ${layers.filter((l) => l.type === 'image').length + 1}`,
        url,
        width: width || placedW,
        height: height || placedH,
        x: sceneW / 2,
        y: sceneH / 2,
      });
      // Pre-set scale so the image visually lands at 80% of the canvas.
      layer.transform.scaleX = ratio;
      layer.transform.scaleY = ratio;
      // Pre-warm the cache; the onLoad bumps `tick` so the render flushes.
      loadImage(url, () => setTick((t) => t + 1));
      pushHistory([...layers, layer]);
      setActiveLayerId(layer.id);
      setTool('transform');
      toast.dismiss(tid);
      toast.success('Image importée');
    } catch (err) {
      toast.dismiss(tid);
      const msg = err?.response?.data?.detail || "Échec de l'import";
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  // ---- AI generation from current canvas ----
  const handleGenerate = async () => {
    if (generating) return;
    if (!stageRef.current) return;
    if (!projectId || !shotId) {
      toast.error("Enregistrez d'abord le plan avant de générer.");
      return;
    }
    setGenerating(true);
    const tid = toast.loading('Génération IA en cours…');
    const tr = transformerRef.current;
    const prevNodes = tr ? tr.nodes() : [];
    if (tr) { tr.nodes([]); tr.getLayer()?.batchDraw(); }
    try {
      // Use JPEG (no alpha) so transparent PNG layers are properly composited
      // with the white background before being sent to the AI.
      // A PNG with alpha=0 areas can be treated as black by some AI models,
      // making detoured characters invisible in the generation.
      const blob = await new Promise((resolve, reject) => {
        try {
          stageRef.current.toBlob({
            mimeType: 'image/jpeg',
            quality: 0.92,
            pixelRatio: 1 / stageScale,
            callback: (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
          });
        } catch (e) { reject(e); }
      });

      const result = await generateFromSceneImage(projectId, shotId, blob, aiPrompt, 'fast');

      // Pre-warm the image cache from the returned base64 so the layer renders
      // immediately without a round-trip fetch.
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const relUrl = result.url;
      const absUrl = relUrl.startsWith('http') ? relUrl : apiBase + relUrl;
      if (result.image_base64) {
        const dataUrl = `data:image/png;base64,${result.image_base64}`;
        const img = new window.Image();
        imageCache.set(absUrl, img);
        img.onload = () => fireImageListeners(absUrl);
        img.src = dataUrl;
      }

      const genLayer = makeImageLayer({
        name: `IA ${layers.filter((l) => l.name.startsWith('IA ')).length + 1}`,
        url: absUrl,
        width: sceneW,
        height: sceneH,
        x: sceneW / 2,
        y: sceneH / 2,
      });
      pushHistory([...layers, genLayer]);
      setActiveLayerId(genLayer.id);
      setTool('transform');
      toast.dismiss(tid);
      toast.success('Image générée !');
    } catch (err) {
      toast.dismiss(tid);
      const msg = err?.response?.data?.detail || 'Échec de la génération';
      toast.error(msg);
    } finally {
      if (tr) { tr.nodes(prevNodes); tr.getLayer()?.batchDraw(); }
      setGenerating(false);
    }
  };

  // ---- Capture: flatten the current canvas and push it to the library
  // (root folder, no AI synthesis — just a B&W conversion server-side). ----
  const handleCapture = async () => {
    if (capturing) return;
    if (!stageRef.current) return;
    setCapturing(true);
    const tid = toast.loading('Capture en cours…');
    const tr = transformerRef.current;
    const previousNodes = tr ? tr.nodes() : [];
    if (tr) { tr.nodes([]); tr.getLayer()?.batchDraw(); }
    try {
      const blob = await new Promise((resolve, reject) => {
        try {
          stageRef.current.toBlob({
            mimeType: 'image/png',
            pixelRatio: 1 / stageScale,
            callback: (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
          });
        } catch (e) { reject(e); }
      });
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const file = new File([blob], `Capture ${stamp}.png`, { type: 'image/png' });
      const created = await importLibraryItems([file], { kind: 'objects' });
      // Reflect immediately in the library panel of the editor (root level).
      setLibraryItems((prev) => [...(created || []), ...prev]);
      toast.dismiss(tid);
      toast.success('Capture ajoutée à la bibliothèque');
    } catch (err) {
      toast.dismiss(tid);
      const msg = err?.response?.data?.detail || 'Échec de la capture';
      toast.error(msg);
    } finally {
      if (tr) { tr.nodes(previousNodes); tr.getLayer()?.batchDraw(); }
      setCapturing(false);
    }
  };

  // ---- Save: flatten via stage.toBlob, then PUT scene + flat ----
  const handleSave = async () => {
    if (!stageRef.current) return;
    setSaving(true);
    // Hide the Transformer overlay during flattening so its handles don't get
    // baked into the exported PNG. We restore it right after.
    const tr = transformerRef.current;
    const previousNodes = tr ? tr.nodes() : [];
    if (tr) {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
    }
    try {
      // Render at native scene size (pixelRatio compensates for the display scale).
      const blob = await new Promise((resolve, reject) => {
        try {
          stageRef.current.toBlob({
            mimeType: 'image/png',
            pixelRatio: 1 / stageScale,
            callback: (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
          });
        } catch (e) { reject(e); }
      });

      const scene = {
        version: SCENE_VERSION,
        size: { w: sceneW, h: sceneH },
        layers,
      };

      const updated = await saveShotScene(projectId, shotId, scene, blob);
      toast.success('Vignette enregistrée');
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Échec de la sauvegarde';
      toast.error(msg);
    } finally {
      if (tr) {
        tr.nodes(previousNodes);
        tr.getLayer()?.batchDraw();
      }
      setSaving(false);
    }
  };

  // ---- Render ----
  const layersFromBottomToTop = layers; // array order = visual stack (bottom → top)

  // Use Radix Dialog primitives directly: shadcn's DialogContent forces
  // max-w-lg + grid + p-6 + an absolute close button that all clip / cover the
  // editor chrome at this size.
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 m-auto bg-background border border-border shadow-2xl rounded-lg overflow-hidden flex flex-col w-[96vw] h-[94vh] max-w-[1700px] max-h-[1100px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
          // Outside-clicks and Escape silently dismiss Radix dialogs by
          // default. In the editor that would discard the in-progress scene
          // without saving — block both, so closing only happens via the
          // explicit Annuler / Enregistrer buttons.
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          data-testid="shot-editor-modal"
        >
          <DialogPrimitive.Title className="sr-only">Éditeur de vignette</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Composez la vignette du plan en plusieurs calques.
          </DialogPrimitive.Description>
        {/* Topbar */}
        <div className="h-12 shrink-0 border-b border-border bg-card flex items-center justify-between px-3">
          <div className="flex items-center gap-1.5">

            {/* ── Annuler / Rétablir ────────────────────────────── */}
            <Button variant="outline" size="sm" className="h-8 w-8 p-0"
              onClick={undo} disabled={past.length === 0} title="Annuler" data-testid="editor-undo">
              <Undo2 className="w-3.5 h-3.5" />
            </Button>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0"
              onClick={redo} disabled={future.length === 0} title="Rétablir" data-testid="editor-redo">
              <Redo2 className="w-3.5 h-3.5" />
            </Button>

            <div className="w-px h-5 bg-border mx-0.5" />

            {/* ── Transformer ───────────────────────────────────── */}
            <Button variant={tool === 'transform' ? 'default' : 'outline'} size="sm"
              className="h-8 gap-1.5 text-xs" onClick={() => setTool('transform')}
              data-testid="editor-tool-transform" title="Transformer — déplacer, redimensionner, pivoter">
              <Scaling className="w-3.5 h-3.5" /> Transformer
            </Button>

            <div className="w-px h-5 bg-border mx-0.5" />

            {/* ── Recadrer ──────────────────────────────────────── */}
            <Button variant={tool === 'crop' ? 'default' : 'outline'} size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => { setTool('crop'); setCropStart(null); setCropCurrent(null); }}
              data-testid="editor-tool-crop" title="Recadrer un calque image (crée de la transparence)">
              <Crop className="w-3.5 h-3.5" /> Recadrer
            </Button>

            <div className="w-px h-5 bg-border mx-0.5" />

            {/* ── Pinceau / Gomme ────────────────────────────────── */}
            <Button variant={tool === 'brush' ? 'default' : 'outline'} size="sm"
              className="h-8 gap-1.5 text-xs" onClick={() => setTool('brush')} data-testid="editor-tool-brush">
              <Brush className="w-3.5 h-3.5" /> Pinceau
            </Button>
            <Button variant={tool === 'eraser' ? 'default' : 'outline'} size="sm"
              className="h-8 gap-1.5 text-xs" onClick={() => setTool('eraser')} data-testid="editor-tool-eraser">
              <Eraser className="w-3.5 h-3.5" /> Gomme
            </Button>

            <div className="w-px h-5 bg-border mx-0.5" />

            {/* ── Miroir V / H ──────────────────────────────────── */}
            <Button variant="outline" size="sm" className="h-8 w-8 p-0"
              onClick={() => flipLayer('v')} disabled={!activeLayerId} title="Miroir vertical (haut ↔ bas)">
              <FlipVertical className="w-3.5 h-3.5" />
            </Button>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0"
              onClick={() => flipLayer('h')} disabled={!activeLayerId} title="Miroir horizontal (gauche ↔ droite)">
              <FlipHorizontal className="w-3.5 h-3.5" />
            </Button>

            <div className="w-px h-5 bg-border mx-0.5" />

            {/* ── Reset recadrage (si crop actif) ───────────────── */}
            {activeLayer?.data?.cropRect && (
              <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-muted-foreground px-2"
                onClick={() => pushHistory(layers.map((l) =>
                  l.id === activeLayerId ? { ...l, data: { ...l.data, cropRect: null } } : l
                ))}
                title="Réinitialiser le recadrage">
                Reset <Crop className="w-3.5 h-3.5" />
              </Button>
            )}
            {/* ── Reset gomme (si eraseStrokes sur calque image) ── */}
            {activeLayer?.type === 'image' && (activeLayer.data?.eraseStrokes?.length ?? 0) > 0 && (
              <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-muted-foreground px-2"
                onClick={() => pushHistory(layers.map((l) =>
                  l.id === activeLayerId ? { ...l, data: { ...l.data, eraseStrokes: [] } } : l
                ))}
                title="Réinitialiser la gomme (restaurer les pixels effacés)">
                Reset <Eraser className="w-3.5 h-3.5" />
              </Button>
            )}

            <div className="w-px h-5 bg-border mx-0.5" />

            {/* ── Taille du pinceau ─────────────────────────────── */}
            <div className="flex items-center gap-2">
              <Label className="text-[11px] text-muted-foreground">Taille</Label>
              <input type="range" min={1} max={48} value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-24" data-testid="editor-brush-size" />
              <span className="text-[11px] tabular-nums text-muted-foreground w-6 text-right">{brushSize}</span>
            </div>

            {/* ── Palette de gris ───────────────────────────────── */}
            <div className="flex items-center gap-1.5 ml-1">
              <Label className="text-[11px] text-muted-foreground">Gris</Label>
              <div className="flex items-center gap-1" data-testid="editor-brush-palette">
                {GRAYSCALE_PALETTE.map((g) => {
                  const active = brushColor.toLowerCase() === g.toLowerCase();
                  return (
                    <button key={g} type="button" title={g} disabled={tool === 'eraser'}
                      onClick={() => setBrushColor(g)}
                      className={`h-5 w-5 rounded-sm border transition-all ${active ? 'ring-2 ring-primary border-primary' : 'border-border/60'} ${tool === 'eraser' ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
                      style={{ background: g }} data-testid={`editor-brush-color-${g}`} />
                  );
                })}
              </div>
            </div>

            <div className="w-px h-5 bg-border mx-0.5" />

            {/* ── Importer une image ────────────────────────────── */}
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
              onClick={handleImportClick} disabled={importing}
              data-testid="editor-import-image" title="Importer une image en niveaux de gris">
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
              Importer
            </Button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImportFile} />

          </div>
          <div className="flex items-center gap-2">
            {/* Blanking dropdown — project-level, baked into the exported flat PNG */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={blanking ? 'default' : 'outline'}
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  title="Blanking (bandes noires) — s'applique à toute la timeline"
                >
                  <RectangleHorizontal className="w-3.5 h-3.5" />
                  {blanking ? blanking : 'Blanking'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuLabel className="text-xs">Format cible (bandes noires)</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className={`text-xs ${!blanking ? 'font-semibold text-primary' : ''}`}
                  onClick={() => onBlankingChange?.(null)}
                >
                  Désactivé
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {BLANKING_RATIOS.map((r) => (
                  <DropdownMenuItem
                    key={r.value}
                    className={`text-xs ${blanking === r.value ? 'font-semibold text-primary' : ''}`}
                    onClick={() => onBlankingChange?.(r.value)}
                  >
                    {r.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => onOpenChange(false)} disabled={saving}>
              <X className="w-3.5 h-3.5" /> Annuler
            </Button>
            <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={handleSave} disabled={saving} data-testid="editor-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Enregistrer
            </Button>
          </div>
        </div>

        <div className="flex-1 flex min-h-0">
          {/* Canvas surface — darker backdrop makes the 16:9 frame obvious */}
          <div
            ref={containerRef}
            className="flex-1 relative bg-neutral-800 overflow-hidden flex items-center justify-center"
            onDragOver={onCanvasDragOver}
            onDrop={onCanvasDrop}
          >
            {stageDims.w > 0 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-wider text-white/40 font-mono">
                16:9 · {sceneW}×{sceneH}
              </div>
            )}
            <Stage
              ref={stageRef}
              width={stageDims.w}
              height={stageDims.h}
              scaleX={stageScale}
              scaleY={stageScale}
              onMouseDown={onStageMouseDown}
              onMouseMove={onStageMouseMove}
              onMouseUp={onStageMouseUp}
              onMouseLeave={onStageMouseUp}
              onTouchStart={onStageMouseDown}
              onTouchMove={onStageMouseMove}
              onTouchEnd={onStageMouseUp}
              style={{
                background: 'white',
                boxShadow: '0 4px 24px rgba(0,0,0,0.45)',
                borderRadius: '4px',
                cursor: tool === 'brush' ? brushCursor : tool === 'eraser' ? eraserCursor : tool === 'crop' ? 'crosshair' : tool === 'transform' ? 'move' : 'default',
              }}
            >
              {/* Solid white backdrop — must live inside the Stage so it gets
                  baked into stage.toBlob(). Without it the flattened PNG keeps
                  a transparent background, which several viewers (including
                  reportlab in the PDF export) render as black. */}
              <KonvaLayer listening={false}>
                <KonvaRect x={0} y={0} width={sceneW} height={sceneH} fill="#ffffff" />
              </KonvaLayer>
              {/* One Konva.Layer per editor layer for proper per-layer erase isolation */}
              {layersFromBottomToTop.map((layer) => {
                if (layer.type === 'image') {
                  const t = layer.transform || { x: sceneW / 2, y: sceneH / 2, rotation: 0, scaleX: 1, scaleY: 1 };
                  const w = layer.data?.width || 0;
                  const h = layer.data?.height || 0;
                  const isActive = layer.id === activeLayerId;
                  const interactive = isActive && (tool === 'transform') && !layer.locked && layer.visible;
                  // Allow eraser events to pass through to the Stage
                  const canErase = isActive && tool === 'eraser' && !layer.locked && layer.visible;
                  const layerId = layer.id;
                  const eraseStrokes = layer.data?.eraseStrokes || [];
                  const cropRect = layer.data?.cropRect || null;
                  const clipFunc = cropRect
                    ? (ctx) => { ctx.rect(cropRect.x, cropRect.y, cropRect.width, cropRect.height); }
                    : undefined;
                  const img = loadImage(layer.data?.url, () => {
                    setTick((tt) => tt + 1);
                    requestAnimationFrame(() => {
                      shapeRefs.current[layerId]?.getLayer()?.draw?.();
                    });
                  });
                  return (
                    // Each image layer lives on its own KonvaLayer canvas.
                    // Erase strokes use destination-out which only affects
                    // THIS canvas, so only the image pixels are erased —
                    // the white background (on its own canvas below) shows through.
                    <KonvaLayer key={layer.id} visible={layer.visible} listening={interactive || canErase}>
                      <KonvaGroup
                        ref={(node) => { shapeRefs.current[layer.id] = node; }}
                        x={t.x}
                        y={t.y}
                        rotation={t.rotation || 0}
                        scaleX={t.scaleX ?? 1}
                        scaleY={t.scaleY ?? 1}
                        offsetX={w / 2}
                        offsetY={h / 2}
                        clipFunc={clipFunc}
                        draggable={interactive}
                        onDragEnd={(e) => {
                          updateLayerTransform(layer.id, { x: e.target.x(), y: e.target.y() });
                        }}
                        onTransformEnd={(e) => {
                          const node = e.target;
                          updateLayerTransform(layer.id, {
                            x: node.x(), y: node.y(),
                            rotation: node.rotation(),
                            scaleX: node.scaleX(), scaleY: node.scaleY(),
                          });
                        }}
                      >
                        {img && (
                          <KonvaImage
                            image={img}
                            x={0} y={0}
                            width={w} height={h}
                            listening={false}
                          />
                        )}
                        {/* Invisible hit rect so Konva can detect clicks/drags on the group.
                            Without it, KonvaImage's listening=false leaves no hit area and
                            the drag never starts (e.target stays the Stage). */}
                        {interactive && (
                          <KonvaRect x={0} y={0} width={w} height={h} fill="black" opacity={0.001} />
                        )}
                        {/* Erase strokes — destination-out composited against the image above */}
                        {eraseStrokes.map((s) => <StrokeShape key={s.id} stroke={s} />)}
                        {/* In-progress erase stroke preview */}
                        {layer.id === activeLayerId && currentStrokeRef.current && (
                          <StrokeShape key={`current_${tick}`} stroke={currentStrokeRef.current} />
                        )}
                      </KonvaGroup>
                    </KonvaLayer>
                  );
                }
                // drawing layer — strokes wrapped in a Group so the whole
                // layer can be moved/scaled/rotated as one node, just like
                // an imported image. Only the active layer is interactive,
                // so clicking elsewhere never silently changes the selection.
                const dt = getTransform(layer);
                const isActive = layer.id === activeLayerId;
                const drawingInteractive = isActive && (tool === 'transform') && !layer.locked && layer.visible;
                const drawingCropRect = layer.data?.cropRect || null;
                const drawingClipFunc = drawingCropRect
                  ? (ctx) => { ctx.rect(drawingCropRect.x, drawingCropRect.y, drawingCropRect.width, drawingCropRect.height); }
                  : undefined;
                return (
                  <KonvaLayer
                    key={layer.id}
                    visible={layer.visible}
                    listening={drawingInteractive}
                  >
                    <KonvaGroup
                      ref={(node) => { shapeRefs.current[layer.id] = node; }}
                      x={dt.x}
                      y={dt.y}
                      rotation={dt.rotation || 0}
                      scaleX={dt.scaleX ?? 1}
                      scaleY={dt.scaleY ?? 1}
                      offsetX={sceneW / 2}
                      offsetY={sceneH / 2}
                      clipFunc={drawingClipFunc}
                      draggable={drawingInteractive}
                      onDragEnd={(e) => {
                        updateLayerTransform(layer.id, {
                          x: e.target.x(),
                          y: e.target.y(),
                        });
                      }}
                      onTransformEnd={(e) => {
                        const node = e.target;
                        updateLayerTransform(layer.id, {
                          x: node.x(),
                          y: node.y(),
                          rotation: node.rotation(),
                          scaleX: node.scaleX(),
                          scaleY: node.scaleY(),
                        });
                      }}
                    >
                      {/* Invisible hit-rect covering the scene gives the
                          Transformer a stable bbox even on an empty drawing
                          layer, and lets the user grab any part of the layer
                          to drag it (not just the strokes themselves). */}
                      {drawingInteractive && (
                        <KonvaRect
                          x={0}
                          y={0}
                          width={sceneW}
                          height={sceneH}
                          fill="black"
                          opacity={0.001}
                        />
                      )}
                      {(layer.data?.strokes || []).map((s) => (
                        <StrokeShape key={s.id} stroke={s} />
                      ))}
                      {layer.id === activeLayerId && currentStrokeRef.current && (
                        <StrokeShape key={`current_${tick}`} stroke={currentStrokeRef.current} />
                      )}
                    </KonvaGroup>
                  </KonvaLayer>
                );
              })}
              {/* Blanking overlay — non-interactive, baked into stage.toBlob export.
                  Renders before the Transformer so handles stay accessible. */}
              {blanking && (() => {
                const bars = blankingBars(blanking, sceneW, sceneH);
                if (!bars) return null;
                return (
                  <KonvaLayer listening={false}>
                    {bars.type === 'letter' ? (
                      <>
                        <KonvaRect x={0} y={0} width={sceneW} height={bars.barH} fill="black" />
                        <KonvaRect x={0} y={sceneH - bars.barH} width={sceneW} height={bars.barH} fill="black" />
                      </>
                    ) : (
                      <>
                        <KonvaRect x={0} y={0} width={bars.barW} height={sceneH} fill="black" />
                        <KonvaRect x={sceneW - bars.barW} y={0} width={bars.barW} height={sceneH} fill="black" />
                      </>
                    )}
                  </KonvaLayer>
                );
              })()}

              {/* Transformer overlay — its own Konva.Layer, always on top */}
              <KonvaLayer>
                <Transformer
                  ref={transformerRef}
                  rotateEnabled
                  flipEnabled={false}
                  anchorSize={10}
                  borderStroke="#3b82f6"
                  borderStrokeWidth={1}
                  anchorStroke="#3b82f6"
                  anchorFill="#ffffff"
                  boundBoxFunc={(oldBox, newBox) => {
                    if (newBox.width < 8 || newBox.height < 8) return oldBox;
                    return newBox;
                  }}
                />
              </KonvaLayer>
              {/* Crop selection overlay — shown while dragging a crop rectangle */}
              {tool === 'crop' && cropStart && cropCurrent && (() => {
                const x = Math.min(cropStart.x, cropCurrent.x);
                const y = Math.min(cropStart.y, cropCurrent.y);
                const rw = Math.abs(cropCurrent.x - cropStart.x);
                const rh = Math.abs(cropCurrent.y - cropStart.y);
                return (
                  <KonvaLayer listening={false}>
                    <KonvaRect
                      x={x} y={y} width={rw} height={rh}
                      stroke="#3b82f6"
                      strokeWidth={1 / stageScale}
                      dash={[5 / stageScale, 3 / stageScale]}
                      fill="rgba(59,130,246,0.08)"
                    />
                  </KonvaLayer>
                );
              })()}
            </Stage>
          </div>

          {/* Layers panel — collapsible to free up canvas width */}
          <div className="shrink-0 border-l border-border bg-card flex flex-col w-60 overflow-hidden">
            {/* AI generation section */}
            <div className="px-2 py-2 border-b border-border space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-muted-foreground" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Génération IA</span>
              </div>
              <textarea
                placeholder="Instructions…"
                className="w-full min-h-[6rem] text-xs rounded border border-border bg-background px-2 py-1.5 placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary resize-y"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                disabled={generating}
              />
              <Button
                size="sm"
                className="w-full h-7 text-xs gap-1"
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Sparkles className="w-3 h-3" />}
                Générer
              </Button>
            </div>

            {/* Layer list doubles as a drop zone for library items.
                The dashed ring lights up while dragging over so the user
                sees the target. */}
            <div
              className={`flex-1 overflow-y-auto p-1.5 space-y-0.5 transition-colors ${layerDropActive ? 'bg-primary/5 ring-1 ring-inset ring-primary/40' : ''}`}
              onDragOver={onLayerListDragOver}
              onDragLeave={onLayerListDragLeave}
              onDrop={onLayerListDrop}
              data-testid="editor-layers-drop"
            >
              {/* Top of the visual stack on top of the list */}
              {[...layers].reverse().map((layer) => (
                <LayerRow
                  key={layer.id}
                  layer={layer}
                  isActive={layer.id === activeLayerId}
                  isDragTarget={dragOverLayerId === layer.id}
                  onSelect={(id) => setActiveLayerId(id)}
                  onToggleVisible={() => toggleVisible(layer.id)}
                  onToggleLock={() => toggleLock(layer.id)}
                  onRename={(name) => renameLayer(layer.id, name)}
                  onDelete={() => deleteLayer(layer.id)}
                  onDragStart={() => setDragLayerId(layer.id)}
                  onDragOver={() => { if (dragLayerId && dragLayerId !== layer.id) setDragOverLayerId(layer.id); }}
                  onDrop={() => {
                    if (dragLayerId && dragLayerId !== layer.id) {
                      const from = layers.findIndex(l => l.id === dragLayerId);
                      const to = layers.findIndex(l => l.id === layer.id);
                      if (from !== -1 && to !== -1) {
                        const next = [...layers];
                        const [moved] = next.splice(from, 1);
                        next.splice(to, 0, moved);
                        pushHistory(next);
                      }
                    }
                    setDragLayerId(null);
                    setDragOverLayerId(null);
                  }}
                  onDragEnd={() => { setDragLayerId(null); setDragOverLayerId(null); }}
                />
              ))}
              {layers.length === 0 && (
                <div className="text-center py-8 text-[11px] text-muted-foreground/70">
                  Aucun calque. Glissez un élément depuis la bibliothèque ou cliquez sur Ajouter.
                </div>
              )}
            </div>
            {/* Capturer + Ajouter un calque — side by side, bottom of the list.
                Capturer flattens the current canvas and pushes it down into
                the library (the down-left arrow hints at that direction). */}
            <div className="flex items-center border-t border-border/50">
              <button
                type="button"
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors disabled:opacity-50"
                onClick={handleCapture}
                disabled={capturing}
                data-testid="editor-capture"
                title="Capturer la vignette et l'ajouter à la bibliothèque"
              >
                {capturing
                  ? <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                  : <ArrowDown className="w-3.5 h-3.5 shrink-0" />}
                Capturer
              </button>
              <div className="w-px h-4 bg-border" />
              <button
                type="button"
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                onClick={addLayer}
                data-testid="editor-add-layer"
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" className="shrink-0">
                  <rect x="0.75" y="0.75" width="11.5" height="11.5" stroke="currentColor" strokeWidth="1.2"/>
                  <line x1="6.5" y1="3.5" x2="6.5" y2="9.5" stroke="currentColor" strokeWidth="1.2"/>
                  <line x1="3.5" y1="6.5" x2="9.5" y2="6.5" stroke="currentColor" strokeWidth="1.2"/>
                </svg>
                Ajouter un calque
              </button>
            </div>

            {/* Library section — accordion style, folders expand in place */}
            {(libraryItems.length > 0 || libraryFolders.length > 0) && (() => {
              const apiBase = process.env.REACT_APP_BACKEND_URL || '';

              const toggleFolder = (id) => setExpandedFolderIds((prev) => {
                const next = new Set(prev);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
              });

              const renderItem = (item) => {
                const src = item.canonical_url?.startsWith('http')
                  ? item.canonical_url : apiBase + (item.canonical_url || '');
                return (
                  <div key={item.id} draggable onDragStart={onLibraryDragStart(item)}
                    className="aspect-video bg-secondary/30 rounded border border-border overflow-hidden cursor-grab active:cursor-grabbing hover:border-primary/40 transition-colors"
                    title={item.name}>
                    {src && <img src={src} alt={item.name} className="w-full h-full object-cover" draggable={false} />}
                  </div>
                );
              };

              // Hidden public folders (and their items) are still rendered,
              // just dimmed. The eye toggle is only meaningful at the folder
              // root — descendants inherit the parent's hidden flag.
              const isFolderHidden = (folder) => {
                if (folder.is_owner !== false) return false; // own folders aren't dimmable
                if (hiddenPublicFolderIds.has(folder.id)) return true;
                let cur = folder.parent_id || null;
                while (cur) {
                  if (hiddenPublicFolderIds.has(cur)) return true;
                  const parent = libraryFolders.find((f) => f.id === cur);
                  cur = parent ? (parent.parent_id || null) : null;
                }
                return false;
              };

              const renderFolder = (folder, depth = 0, opts = {}) => {
                const expanded = expandedFolderIds.has(folder.id);
                const children = libraryFolders.filter((f) => f.parent_id === folder.id);
                const folderItems = libraryItems.filter((it) => (it.folder_id || null) === folder.id);
                const isDropTarget = libraryDropTargetId === folder.id;
                const dimmed = opts.dimmed || isFolderHidden(folder);
                const isPublicTopLevel = opts.publicTopLevel;
                const ownedByMe = folder.is_owner !== false;
                return (
                  <div key={folder.id} className={dimmed ? 'opacity-40' : ''}>
                    <div className="flex items-center group">
                      <button type="button"
                        className={`flex-1 flex items-center gap-1 py-1 text-[11px] hover:text-foreground hover:bg-secondary/40 transition-colors text-left rounded ${
                          isDropTarget
                            ? 'bg-primary/10 text-primary ring-1 ring-primary/40'
                            : 'text-muted-foreground'
                        }`}
                        style={{ paddingLeft: `${6 + depth * 10}px` }}
                        onClick={() => toggleFolder(folder.id)}
                        onDragOver={ownedByMe ? onLibraryFolderDragOver(folder.id) : undefined}
                        onDragLeave={ownedByMe ? onLibraryFolderDragLeave(folder.id) : undefined}
                        onDrop={ownedByMe ? onLibraryFolderDrop(folder.id) : undefined}>
                        <ChevronRight className={`w-3 h-3 shrink-0 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`} />
                        <Folder className="w-3 h-3 shrink-0" />
                        <span className="truncate">{folder.name}</span>
                      </button>
                      {/* Hide-toggle is only offered on top-level public folders;
                          descendants inherit visibility so the toggle doesn't
                          have to be repeated on every level. */}
                      {isPublicTopLevel && (
                        <button type="button"
                          className="px-1 py-1 text-muted-foreground/70 hover:text-foreground transition-colors"
                          onClick={(e) => { e.stopPropagation(); togglePublicFolderHidden(folder.id); }}
                          title={dimmed ? 'Afficher pleinement' : 'Griser ce dossier'}>
                          {dimmed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                    {expanded && (
                      <div style={{ paddingLeft: `${6 + (depth + 1) * 10}px` }}>
                        {children.map((f) => renderFolder(f, depth + 1, { dimmed }))}
                        {folderItems.length > 0 && (
                          <div className="grid grid-cols-3 gap-1 pr-1 py-1">
                            {folderItems.map(renderItem)}
                          </div>
                        )}
                        {children.length === 0 && folderItems.length === 0 && (
                          <p className="text-[10px] text-muted-foreground/50 py-1">Vide</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              const rootFolders = libraryFolders.filter((f) => (f.parent_id || null) === null);
              const ownedRootFolders = rootFolders.filter((f) => f.is_owner !== false);
              const publicRootFolders = rootFolders.filter((f) => f.is_owner === false && f.is_public);
              const rootItems = libraryItems.filter((it) => (it.folder_id || null) === null && it.is_owner !== false);
              // Public root-level items (admin items not in any folder) are
              // shown alongside public root folders so the section is complete.
              const publicRootItems = libraryItems.filter((it) => (it.folder_id || null) === null && it.is_owner === false);

              const rootIsDropTarget = libraryDropTargetId === 'root';
              return (
                <div className="border-t border-border max-h-[45%] overflow-y-auto">
                  <div
                    className={`px-2 py-1.5 flex items-center gap-1 sticky top-0 border-b border-border/50 transition-colors ${
                      rootIsDropTarget ? 'bg-primary/10 ring-1 ring-primary/40' : 'bg-card'
                    }`}
                    onDragOver={onLibraryFolderDragOver(null)}
                    onDragLeave={onLibraryFolderDragLeave(null)}
                    onDrop={onLibraryFolderDrop(null)}
                  >
                    <LibraryIcon className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Bibliothèque</span>
                  </div>
                  <div className="px-1.5 py-1.5">
                    {ownedRootFolders.map((f) => renderFolder(f))}
                    {rootItems.length > 0 && (
                      <div className="grid grid-cols-3 gap-1 pt-1">
                        {rootItems.map(renderItem)}
                      </div>
                    )}
                    {ownedRootFolders.length === 0 && rootItems.length === 0 && publicRootFolders.length === 0 && publicRootItems.length === 0 && (
                      <p className="text-center text-[11px] text-muted-foreground/60 py-4">Bibliothèque vide</p>
                    )}
                  </div>
                  {(publicRootFolders.length > 0 || publicRootItems.length > 0) && (
                    <>
                      <div className="px-2 py-1 flex items-center gap-1 border-t border-b border-border/50 bg-secondary/20">
                        <LibraryIcon className="w-3 h-3 text-muted-foreground" />
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Partagés</span>
                      </div>
                      <div className="px-1.5 py-1.5">
                        {publicRootFolders.map((f) => renderFolder(f, 0, { publicTopLevel: true }))}
                        {publicRootItems.length > 0 && (
                          <div className="grid grid-cols-3 gap-1 pt-1">
                            {publicRootItems.map(renderItem)}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
